import React, { useState, useCallback, useMemo } from 'react';
import { X, Check } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Step1Details } from './Step1Details';
import { Step2Participants } from './Step2Participants';
import { Step3Summary } from './Step3Summary';
import { Step4Payment } from './Step4Payment';
import { isPaymentEnabled } from '../../utils/mockHelper';
import { tripService } from '../../services/trips';

export interface TripFormData {
  // Step 1: Trip Details
  hotel_name: string;
  start_date: string;
  end_date: string;
  
  // Step 2: Participants
  participants: Array<{
    id?: number; // For saved companions
    name: string;
    email?: string;
    document_number: string;
    date_of_birth: string;
    phone?: string;
    country_code?: string;
    exemption_reason_id?: number;
    is_saved_companion: boolean;
  }>;
  
  // Step 3: Summary (calculated)
  total_days?: number;
  total_amount?: number;
  exemptions_applied?: number;
  
  // Step 4: Payment
  payment_method?: 'credit_card' | 'debit_card' | 'pix';
  payment_details?: any;
}

interface TripWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (tripId: number) => void;
}

const STEPS = [
  { id: 1, title: 'Detalhes da Viagem', description: 'Hotel e datas' },
  { id: 2, title: 'Participantes', description: 'Quem vai viajar' },
  { id: 3, title: 'Resumo', description: 'Conferir dados' },
  { id: 4, title: 'Pagamento', description: 'Finalizar' },
];

export function TripWizardModal({ isOpen, onClose, onSuccess }: TripWizardModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<TripFormData>({
    hotel_name: '',
    start_date: '',
    end_date: '',
    participants: [],
  });

  // Determinar passos baseado na configuração de pagamento
  const activeSteps = useMemo(() => {
    const paymentEnabled = isPaymentEnabled();
    return paymentEnabled ? STEPS : STEPS.slice(0, 3);
  }, []);

  const totalSteps = activeSteps.length;

  const updateFormData = useCallback((data: Partial<TripFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setCurrentStep(1);
      setFormData({
        hotel_name: '',
        start_date: '',
        end_date: '',
        participants: [],
      });
      onClose();
    }
  };

  const handleSuccess = (tripId: number) => {
    handleClose();
    onSuccess?.(tripId);
  };

  const handleTripSubmit = async () => {
    // Esta função será chamada quando o pagamento estiver desabilitado
    setIsLoading(true);
    try {
      // Criar viagem sem pagamento (status pendente)
      const tripPayload = {
        hotel_name: formData.hotel_name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        participants: formData.participants.map(p => ({
          name: p.name,
          email: p.email,
          document_number: p.document_number,
          date_of_birth: p.date_of_birth,
          phone: p.phone,
          country_code: p.country_code,
          exemption_reason_id: p.exemption_reason_id
        }))
      };

      const newTrip = await tripService.createTrip(tripPayload);
      handleSuccess(newTrip.id);
    } catch (error) {
      console.error('Erro ao criar viagem:', error);
      // Aqui você poderia mostrar uma mensagem de erro para o usuário
    } finally {
      setIsLoading(false);
    }
  };

  const isStepCompleted = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.hotel_name && formData.start_date && formData.end_date);
      case 2:
        return formData.participants.length > 0;
      case 3:
        return true; // Summary is always "complete" when reached
      case 4:
        return false; // Payment step is never pre-completed
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Details
            data={formData}
            onChange={updateFormData}
            onNext={nextStep}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Step2Participants
            data={formData}
            onChange={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
            isLoading={isLoading}
          />
        );
      case 3:
        return (
          <Step3Summary
            data={formData}
            onChange={updateFormData}
            onNext={isPaymentEnabled() ? nextStep : handleTripSubmit}
            onPrev={prevStep}
            isLoading={isLoading}
            isLastStep={!isPaymentEnabled()}
          />
        );
      case 4:
        return (
          <Step4Payment
            data={formData}
            onChange={updateFormData}
            onPrev={prevStep}
            onSuccess={handleSuccess}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nova Viagem"
      size="xl"
      closable={!isLoading}
    >
      <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
        {/* Progress Steps */}
        <div className="relative">
          <div className="flex items-center justify-between px-2 sm:px-4 overflow-x-auto scrollbar-hide">
            {activeSteps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center space-y-2 min-w-0 flex-shrink-0">
                  {/* Step Circle */}
                  <div
                    className={`
                      flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 transition-all duration-200
                      ${currentStep === step.id
                        ? 'border-blue-500 bg-blue-500 text-white shadow-lg scale-110'
                        : currentStep > step.id
                        ? 'border-green-500 bg-green-500 text-white'
                        : 'border-gray-300 bg-white text-gray-500'
                      }
                    `}
                  >
                    {currentStep > step.id ? (
                      <Check className="h-4 w-4 sm:h-6 sm:w-6" />
                    ) : (
                      <span className="text-xs sm:text-sm font-bold">{step.id}</span>
                    )}
                  </div>

                  {/* Step Labels */}
                  <div className="text-center min-w-0 max-w-[70px] sm:max-w-none">
                    <div
                      className={`text-xs sm:text-sm font-medium truncate ${
                        currentStep === step.id ? 'text-blue-600' : 'text-gray-900'
                      }`}
                      title={step.title}
                    >
                      {step.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 hidden sm:block truncate">{step.description}</div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < activeSteps.length - 1 && (
                  <div className="flex-1 mx-1 sm:mx-4 min-w-[15px] max-w-[60px] sm:max-w-none">
                    <div
                      className={`h-0.5 transition-all duration-200 ${
                        currentStep > step.id ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="min-h-[400px] px-1 sm:px-2">
          {renderStepContent()}
        </div>
      </div>
    </Modal>
  );
}

export default TripWizardModal;
